/**
 * Detect whether a game dist uses the canonical launch entry (game-launch.js)
 * or the temporary legacy develop-era entry (assets/index-*.js).
 *
 * LEGACY path is temporary — remove detectClientLaunchFormat and all 'legacy'
 * branches once every registry game has been rebuilt. See:
 * docs/ai-engineering/LEGACY_LAUNCH_FLOW.md
 */

import { fetchWithTimeout } from '../../../../client/libraries/atemoya-contracts/src/fetchWithTimeout';

export type ClientLaunchFormat = 'canonical' | 'legacy';

const formatCache = new Map<string, ClientLaunchFormat>();

const FORCE_FORMAT_PARAM = 'force_launch_format';

function cacheKeyFromGameUrl(gameUrl: string): string {
    try {
        const url = new URL(gameUrl, window.location.origin);
        return `${url.origin}${url.pathname}`;
    } catch {
        return gameUrl;
    }
}

function parseFormatFromHtml(html: string): ClientLaunchFormat {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const moduleScripts = [...doc.querySelectorAll('script[type="module"][src]')];
    const hasGameLaunch = moduleScripts.some((script) => {
        const src = script.getAttribute('src') ?? '';
        return src.includes('game-launch');
    });
    return hasGameLaunch ? 'canonical' : 'legacy';
}

function readForcedFormat(): ClientLaunchFormat | null {
    try {
        const fromQuery = new URLSearchParams(window.location.search).get(FORCE_FORMAT_PARAM);
        if (fromQuery === 'canonical' || fromQuery === 'legacy') {
            return fromQuery;
        }
    } catch {
        // ignore
    }
    return null;
}

/**
 * Fetch game index.html and resolve launch format. Results are cached per pathname.
 */
export async function detectClientLaunchFormat(gameUrl: string): Promise<ClientLaunchFormat> {
    const forced = readForcedFormat();
    if (forced) {
        return forced;
    }

    const key = cacheKeyFromGameUrl(gameUrl);
    const cached = formatCache.get(key);
    if (cached) {
        return cached;
    }

    let indexUrl: string;
    try {
        const url = new URL(gameUrl, window.location.origin);
        indexUrl = `${url.origin}${url.pathname}`;
    } catch {
        indexUrl = gameUrl;
    }

    const response = await fetchWithTimeout(indexUrl);
    if (!response.ok) {
        throw new Error(
            `[detectClientLaunchFormat] Failed to load ${indexUrl} (${response.status})`
        );
    }

    const html = await response.text();
    let format = parseFormatFromHtml(html);

    // Hybrid dists: index.html may still reference assets/index-*.js while game-launch.js exists.
    // Prefer the canonical artifact so we do not load incomplete legacy Vite chunk graphs.
    try {
        const launchUrl = new URL('./game-launch.js', indexUrl).href;
        const head = await fetchWithTimeout(launchUrl, { method: 'HEAD' }, 5000);
        if (format === 'legacy' && head.ok) {
            format = 'canonical';
        }
    } catch {
        // Keep HTML-derived format when the probe fails or times out.
    }

    formatCache.set(key, format);
    return format;
}

/** Clear in-memory cache (tests / forced re-detection). */
export function clearClientLaunchFormatCache(): void {
    formatCache.clear();
}

/** Sync parse helper for unit tests / prepare-script parity checks. */
export function parseClientLaunchFormatFromHtml(html: string): ClientLaunchFormat {
    return parseFormatFromHtml(html);
}
